import { MethodType, MethodTypeRepository, Resource, ResourceTypes } from '@telespot/domain';
import { MethodTypeConfigService } from '@protocols/domain';

export interface SaveMethodTypeInput {
  /** Undefined = create; the repository assigns the id. */
  id?: string;
  name: string;
  assetTypeId: string;
  /** One localized record per predefined sample name. */
  sampleNames: Record<string, string>[];
  /**
   * Hand-edited config blob from the editor's manual JSON mode. When present
   * it is persisted verbatim as the SOLE config and `sampleNames` is ignored;
   * when absent, config is rebuilt from `sampleNames` as `{ defaultSamples }`.
   */
  config?: Record<string, unknown>;
  acquisitionFields: { displayName: string; fieldPath: string }[];
  /** Ids of the resources assigned to the method type (persisted as pointers). */
  resourceIds: string[];
}

export interface SaveMethodTypeOutput {
  methodTypeId: string;
}

export enum SaveMethodTypeErrorCode {
  MISSING_NAME = 'A non-blank `name` is required to save a method type.',
  MISSING_ASSET_TYPE_ID = 'An `assetTypeId` is required to save a method type.',
  MISSING_PERSISTED_ID = 'MethodTypeRepository.save() returned an empty id.',
}

export class SaveMethodTypeException extends Error {
  constructor(public readonly code: SaveMethodTypeErrorCode) {
    super(code.toString());
    this.name = 'SaveMethodTypeException';
    Object.setPrototypeOf(this, SaveMethodTypeException.prototype);
  }
}

export interface ISaveMethodTypeCommand {
  execute(input: SaveMethodTypeInput): Promise<SaveMethodTypeOutput>;
}

/**
 * Persists the method-type ROW only — `ProtocolPipeline` joins belong to
 * `SaveAssignedPipelinesCommand` so a join failure never orphans a created row.
 *
 * The `config` is rebuilt from the editor input on every save: it owns ONLY
 * `defaultSamples`, so a sample removed in a later edit is truly gone and no
 * stale config keys linger — UNLESS the editor hands over a manual `config`
 * override (its JSON manual mode), which is persisted verbatim as the sole
 * config. The row's `analysisTypes` are carried over untouched.
 */
export class SaveMethodTypeCommand implements ISaveMethodTypeCommand {
  constructor(private readonly methodTypeRepository: MethodTypeRepository) {}

  public async execute(input: SaveMethodTypeInput): Promise<SaveMethodTypeOutput> {
    if (input.name.trim() === '') {
      throw new SaveMethodTypeException(SaveMethodTypeErrorCode.MISSING_NAME);
    }
    if (!input.assetTypeId) {
      throw new SaveMethodTypeException(SaveMethodTypeErrorCode.MISSING_ASSET_TYPE_ID);
    }

    const existing = input.id ? await this.methodTypeRepository.getById(input.id) : undefined;

    const config = input.config ?? MethodTypeConfigService.buildConfig(input.sampleNames);

    const methodType = new MethodType({
      id: input.id,
      name: input.name,
      config,
      assetTypeId: input.assetTypeId,
      assetDataFields: input.acquisitionFields.map((field) => ({ ...field })),
      analysisTypes: existing?.analysisTypes,
      resources: this.toResourceStubs(input.resourceIds),
      createdAt: existing?.createdAt,
      updatedAt: existing?.updatedAt,
      deletedAt: existing?.deletedAt,
    });

    const methodTypeId = await this.methodTypeRepository.save(methodType);
    if (!methodTypeId) {
      throw new SaveMethodTypeException(SaveMethodTypeErrorCode.MISSING_PERSISTED_ID);
    }

    return { methodTypeId };
  }

  /*
   * The repository serializes `resources` as pointers — only the id reaches the
   * row — so minimal stub entities are enough to express the reference list;
   * every other field value is irrelevant filler.
   */
  private toResourceStubs(resourceIds: string[]): Resource[] {
    return resourceIds.map(
      (id) =>
        new Resource({
          id,
          name: '',
          type: ResourceTypes.image,
          resources: [],
          version: 1,
          description: '',
          value: '',
        })
    );
  }
}
